import { format, isValid } from 'date-fns';

/** HTML-escape a value for safe injection into HTML template strings (XSS prevention). */
export const escHtml = (v: unknown): string => {
  const s = v == null ? '' : String(v);
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;').replace(/'/g, '&#x27;');
};

const normalizeDateInput = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  let normalized = String(value).trim();
  // Strip wrapping quotes if any
  if ((normalized.startsWith('"') && normalized.endsWith('"')) || (normalized.startsWith("'") && normalized.endsWith("'"))) {
    normalized = normalized.slice(1, -1);
  }
  // Allow space-separated date-times by replacing first space with T
  if (normalized.includes(' ') && !normalized.includes('T')) {
    normalized = normalized.replace(' ', 'T');
  }
  // Trim nanosecond precision to milliseconds for JS Date parsing
  normalized = normalized.replace(/\.(\d{3})\d+/, '.$1');
  return normalized;
};

export const parseToDate = (date: unknown): Date | null => {
  if (date instanceof Date) return isValid(date) ? date : null;
  
  // Handle Java LocalDateTime array: [year, month, day, hour, minute, second, nanoseconds]
  if (Array.isArray(date)) {
    const parts = (date as unknown[]).map((p) => Number(p));
    if (parts.length >= 3) {
      const [y, m, d, hh = 0, mm = 0, ss = 0, nano = 0] = parts;
      const ms = Math.floor(nano / 1_000_000); // nanoseconds → milliseconds
      const constructed = new Date(y, (m || 1) - 1, d || 1, hh || 0, mm || 0, ss || 0, ms);
      return isValid(constructed) ? constructed : null;
    }
  }
  
  if (typeof date === 'number') {
    const dNum = new Date(String(date).length === 10 ? date * 1000 : date);
    return isValid(dNum) ? dNum : null;
  }
  
  const raw = normalizeDateInput(date);

  // Numeric epoch (ms or s) - but avoid false positives from stringified arrays
  if (/^\d+$/.test(raw) && raw.length !== 12) { // 12 digits could be array concatenation
    const num = Number(raw);
    const d = new Date(raw.length === 10 ? num * 1000 : num);
    return isValid(d) ? d : null;
  }

  // First attempt
  let d = new Date(raw);
  if (isValid(d)) return d;

  // Try appending Z if missing timezone
  if (!raw.toUpperCase().endsWith('Z') && !/[+-]\d\d:?\d\d$/.test(raw)) {
    d = new Date(`${raw}Z`);
    if (isValid(d)) return d;
  }

  return null;
};

export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
  }).format(amount);
}

export function formatSafe(date: string | Date | null | undefined, pattern: string = 'MMM dd, yyyy'): string {
  if (date === null || date === undefined || date === '') return 'N/A';
  const d = parseToDate(date as any);
  if (d) return format(d, pattern);
  // Fall back to the raw string if parsing fails but value exists
  return typeof date === 'string' ? normalizeDateInput(date) || 'N/A' : 'N/A';
}

export function normalizeArray<T>(value: unknown): T[] {
  if (Array.isArray(value)) {
    return value as T[];
  }
  if (value && typeof value === 'object') {
    const maybeData = (value as { data?: unknown }).data;
    if (Array.isArray(maybeData)) {
      return maybeData as T[];
    }
  }
  return [];
}

/**
 * Patient `allergies` / `chronicConditions` are stored on the backend as a single
 * encrypted String (comma/newline-separated), but some UI treats them as string[].
 * Normalize either shape into a clean string[] so `.join`/`.map`/`.length` are safe.
 */
export function normalizeAllergies(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((v) => String(v).trim()).filter(Boolean);
  }
  if (typeof value === 'string') {
    return value.split(/[,;\n]/).map((v) => v.trim()).filter(Boolean);
  }
  return [];
}

export function formatDate(date: string | Date | any): string {
  if (!date) return 'N/A';
  const parsed = parseToDate(date);
  if (parsed) {
    // Format for India: DD MMM YYYY (e.g., 08 Mar 2026)
    return parsed.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  }
  return 'N/A';
}

export function formatDateTime(date: string | Date | any): string {
  if (!date) return 'N/A';
  const parsed = parseToDate(date);
  if (parsed) {
    // Format for India: DD MMM YYYY, HH:MM (e.g., 08 Mar 2026, 20:34)
    const dateStr = parsed.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
    const timeStr = parsed.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    return `${dateStr}, ${timeStr}`;
  }
  return 'N/A';
}

export function formatDateTimeWithSeconds(date: string | Date | any): string {
  if (!date) return 'N/A';
  const parsed = parseToDate(date);
  if (parsed) {
    // Format for India: DD MMM YYYY, HH:MM:SS
    const dateStr = parsed.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
    const timeStr = parsed.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
    return `${dateStr}, ${timeStr}`;
  }
  return 'N/A';
}

export function formatTime(time: string | Date | null | undefined): string {
  if (!time) return 'N/A';
  
  try {
    let d: Date;
    
    // Handle Date objects
    if (time instanceof Date) {
      d = time;
    } else {
      // Handle ISO datetime strings (e.g., "2026-02-28T12:30:00" or "2026-02-28T12:30:00Z")
      const timeString = normalizeDateInput(String(time));
      
      if (timeString.includes('T')) {
        // Full ISO datetime
        const normalizedDate = timeString.endsWith('Z') 
          ? timeString.slice(0, -1) + '+00:00' 
          : timeString;
        d = new Date(normalizedDate);
      } else if (timeString.includes(':')) {
        // Time-only string (HH:MM or HH:MM:SS)
        // Parse and create a Date object
        const parts = timeString.split(':');
        const hours = parseInt(parts[0], 10);
        const minutes = parseInt(parts[1], 10);
        d = new Date();
        d.setHours(hours, minutes, 0, 0);
      } else {
        console.warn('Unsupported time format:', timeString);
        return '';
      }
    }
    
    if (isNaN(d.getTime())) {
      return typeof time === 'string' ? normalizeDateInput(time) || 'N/A' : 'N/A';
    }
    
    const hours = d.getHours();
    const minutes = d.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  } catch (error) {
    return typeof time === 'string' ? normalizeDateInput(time) || 'N/A' : 'N/A';
  }
}

export function generateTimeSlots(startHour: number = 9, endHour: number = 18, interval: number = 5) {
  const slots = [];
  for (let h = startHour; h < endHour; h++) {
    for (let m = 0; m < 60; m += interval) {
      slots.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
    }
  }
  return slots;
}

export function maskMobileNumber(mobile: string): string {
  return mobile.replace(/(\d{6})(\d{4})/, '$1****');
}

export function getNextTokenNumber(lastToken: number): number {
  return lastToken + 1;
}

export function calculateGST(amount: number, gstPercent: number = 18): number {
  return (amount * gstPercent) / 100;
}

export function getTodayDate(): string {
  // Return local date in YYYY-MM-DD format (avoid timezone issues)
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Parse a date string (YYYY-MM-DD) as local date, not UTC
 * This prevents timezone offset issues where selected dates show as one day less
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Date object in local timezone
 */
export function parseLocalDate(dateString: string): Date {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
}

/**
 * Convert any date format (string, Date, array, etc.) to ISO date string "YYYY-MM-DD"
 * Handles Java date arrays [year, month, day], datetime arrays, Date objects, and ISO strings
 * @param date - Date in any format
 * @returns ISO date string "YYYY-MM-DD" or null if invalid
 */
export function toISODateString(date: unknown): string | null {
  if (date === null || date === undefined) return null;

  const parsed = parseToDate(date);
  if (!parsed) return null;

  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

/**
 * Convert time array [hour, minute] or [hour, minute, second] to HH:MM or HH:MM:SS string
 * Handles: [9, 0] -> "09:00", [12, 30, 45] -> "12:30:45"
 * @param time - Time in array format or string
 * @returns HH:MM or HH:MM:SS format string
 */
export function toTimeString(time: unknown): string | null {
  if (time === null || time === undefined) return null;

  // Already a string
  if (typeof time === 'string') return time;

  // Array format [hour, minute] or [hour, minute, second]
  if (Array.isArray(time)) {
    const [hour = 0, minute = 0, second] = time.map(Number);
    const h = String(hour).padStart(2, '0');
    const m = String(minute).padStart(2, '0');

    if (second !== undefined && second !== 0) {
      const s = String(second).padStart(2, '0');
      return `${h}:${m}:${s}`;
    }
    return `${h}:${m}`;
  }

  return null;
}

/**
 * Convert time range array objects to TimeRange strings
 * Handles: { startTime: [9, 0], endTime: [10, 0] } -> { startTime: "09:00", endTime: "10:00" }
 */
export function toTimeRangeStrings(timeRange: any): { startTime: string; endTime: string } | null {
  if (!timeRange) return null;

  const startTime = toTimeString(timeRange.startTime);
  const endTime = toTimeString(timeRange.endTime);

  if (!startTime || !endTime) return null;

  return { startTime, endTime };
}

/**
 * Format time slot string "HHMM-HHMM" or "HH:MM-HH:MM" to "HH:MM - HH:MM"
 * Handles: "90-915" -> "09:00 - 09:15", "09:00-09:15" -> "09:00 - 09:15"
 */
export function formatTimeSlot(timeSlot: string | null | undefined): string {
  if (!timeSlot) return 'N/A';

  // Split by dash
  const [startStr, endStr] = timeSlot.split('-');
  if (!startStr || !endStr) return timeSlot;

  // Format each time part
  const formatTime = (timeStr: string): string => {
    const clean = timeStr.trim();

    // Already in HH:MM format
    if (clean.includes(':')) return clean;

    // Convert from HHMM or HMM to HH:MM
    // Remove leading zeros that shouldn't be there (90 -> 09, 915 -> 09:15)
    const digits = clean.replace(/\D/g, '');

    if (digits.length === 3) {
      // HMM -> HH:MM (515 -> 05:15)
      return `0${digits[0]}:${digits[1]}${digits[2]}`;
    } else if (digits.length === 4) {
      // HHMM -> HH:MM (0900 -> 09:00, 0915 -> 09:15)
      return `${digits[0]}${digits[1]}:${digits[2]}${digits[3]}`;
    } else if (digits.length === 2) {
      // MM -> HH:MM (just minutes, prepend 0)
      return `00:${digits}`;
    }

    return clean;
  };

  const start = formatTime(startStr);
  const end = formatTime(endStr);

  return `${start} - ${end}`;
}

/**
 * Convert 24-hour time format (HH:MM) to 12-hour AM/PM format (HH:MM AM/PM)
 * @param time Time string in HH:MM or H:MM format (e.g., "14:30" or "9:15")
 * @returns Formatted time in 12-hour format with AM/PM (e.g., "02:30 PM" or "09:15 AM")
 */
export function formatTo12Hour(time: string | null | undefined): string {
  if (!time) return 'N/A';

  try {
    const timeTrim = String(time).trim();

    // Handle HH:MM format
    const parts = timeTrim.split(':');
    if (parts.length < 2) return timeTrim;

    let hours = parseInt(parts[0], 10);
    const minutes = parts[1];

    if (isNaN(hours)) return timeTrim;

    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;

    return `${displayHours.toString().padStart(2, '0')}:${minutes} ${ampm}`;
  } catch (error) {
    return String(time) || 'N/A';
  }
}

export function generateBillNumber(_clinicId: string, dateStr: string): string {
  // Format: BILL-YYYY-MM-DDT-TIMESTAMP
  const date = new Date(dateStr);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const timestamp = Date.now().toString().slice(-6);
  return `BILL-${year}-${month}${day}-${timestamp}`;
}

export function formatInvoice(data: {
  billNumber: string;
  clinicName: string;
  clinicAddress?: string;
  clinicPhone?: string;
  gstNumber?: string;
  patientName: string;
  patientMobile: string;
  patientAge?: number;
  patientGender?: string;
  patientAddress?: string;
  doctorName: string;
  visitDate: string;
  billTime?: string;
  chiefComplaint?: string;
  lineItems: Array<{
    itemName: string;
    quantity: number;
    rate: number;
    amount: number;
  }>;
  subtotal: number;
  gstAmount: number;
  totalAmount: number;
  paymentMode: string;
}): string {
  const formattedDate = formatDate(data.visitDate);
  const formattedTime = data.billTime ? data.billTime : formatTime(new Date());

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>Invoice - ${data.billNumber}</title>
      <style>
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        body {
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          background: #f5f5f5;
          padding: 20px;
        }
        .invoice-container {
          background: white;
          max-width: 900px;
          margin: 0 auto;
          padding: 40px;
          box-shadow: 0 0 10px rgba(0,0,0,0.1);
        }
        .header {
          text-align: center;
          margin-bottom: 30px;
          border-bottom: 2px solid #333;
          padding-bottom: 20px;
        }
        .clinic-name {
          font-size: 28px;
          font-weight: bold;
          color: #333;
          margin-bottom: 5px;
        }
        .clinic-details {
          font-size: 13px;
          color: #666;
          line-height: 1.6;
        }
        .invoice-title {
          font-size: 18px;
          font-weight: bold;
          color: #333;
          margin-top: 15px;
          margin-bottom: 5px;
        }
        .invoice-meta {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
          margin-bottom: 30px;
          font-size: 13px;
        }
        .meta-section {
          background: #f9f9f9;
          padding: 12px;
          border-left: 3px solid #2196F3;
        }
        .meta-label {
          font-weight: bold;
          color: #333;
          margin-bottom: 3px;
        }
        .meta-value {
          color: #666;
        }
        .items-table {
          width: 100%;
          border-collapse: collapse;
          margin: 20px 0;
          font-size: 13px;
        }
        .items-table th {
          background: #2196F3;
          color: white;
          padding: 12px;
          text-align: left;
          font-weight: bold;
        }
        .items-table td {
          padding: 10px 12px;
          border-bottom: 1px solid #ddd;
        }
        .items-table tr:hover {
          background: #f9f9f9;
        }
        .text-right {
          text-align: right;
        }
        .totals {
          margin-top: 20px;
          padding-top: 15px;
          border-top: 2px solid #ddd;
        }
        .total-row {
          display: flex;
          justify-content: space-between;
          padding: 8px 0;
          font-size: 13px;
        }
        .total-row.subtotal {
          color: #666;
        }
        .total-row.gst {
          color: #666;
        }
        .total-row.grand-total {
          font-size: 16px;
          font-weight: bold;
          color: #333;
          padding: 12px 0;
          border-top: 2px solid #333;
        }
        .footer {
          margin-top: 30px;
          text-align: center;
          font-size: 12px;
          color: #999;
          border-top: 1px solid #ddd;
          padding-top: 15px;
        }
        .payment-mode {
          background: #f0f7ff;
          padding: 10px;
          margin-top: 15px;
          border-left: 3px solid #2196F3;
          font-size: 13px;
        }
        @media print {
          body {
            background: white;
            padding: 0;
          }
          .invoice-container {
            box-shadow: none;
            padding: 0;
          }
          .invoice-container {
            page-break-after: always;
          }
        }
      </style>
    </head>
    <body>
      <div class="invoice-container">
        <div class="header">
          <div class="clinic-name">${escHtml(data.clinicName)}</div>
          <div class="clinic-details">
            ${data.clinicAddress ? `${escHtml(data.clinicAddress)}<br>` : ''}
            ${data.clinicPhone ? `Phone: ${escHtml(data.clinicPhone)}<br>` : ''}
            ${data.gstNumber ? `GST: ${escHtml(data.gstNumber)}` : ''}
          </div>
          <div class="invoice-title">MEDICAL INVOICE</div>
          <div style="font-size: 12px; color: #999; margin-top: 5px;">
            Invoice No: <strong>${escHtml(data.billNumber)}</strong> | Date: ${escHtml(formattedDate)} | Time: ${escHtml(formattedTime)}
          </div>
        </div>

        <div class="invoice-meta">
          <div class="meta-section">
            <div class="meta-label">PATIENT INFORMATION</div>
            <div class="meta-value"><strong>${escHtml(data.patientName)}</strong></div>
            <div class="meta-value">Mobile: ${escHtml(data.patientMobile)}</div>
            ${data.patientAge ? `<div class="meta-value">Age: ${Number(data.patientAge)} years</div>` : ''}
            ${data.patientGender ? `<div class="meta-value">Gender: ${escHtml(data.patientGender)}</div>` : ''}
            ${data.patientAddress ? `<div class="meta-value">Address: ${escHtml(data.patientAddress)}</div>` : ''}
          </div>
          <div class="meta-section">
            <div class="meta-label">CONSULTATION DETAILS</div>
            <div class="meta-value"><strong>Doctor:</strong> ${escHtml(data.doctorName)}</div>
            <div class="meta-value"><strong>Date:</strong> ${escHtml(formattedDate)}</div>
            ${data.chiefComplaint ? `<div class="meta-value"><strong>Chief Complaint:</strong> ${escHtml(data.chiefComplaint)}</div>` : ''}
          </div>
        </div>

        <table class="items-table">
          <thead>
            <tr>
              <th>Description</th>
              <th class="text-right">Qty</th>
              <th class="text-right">Rate</th>
              <th class="text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            ${data.lineItems
              .map(
                (item) => `
              <tr>
                <td>${escHtml(item.itemName)}</td>
                <td class="text-right">${Number(item.quantity)}</td>
                <td class="text-right">${formatCurrency(item.rate)}</td>
                <td class="text-right"><strong>${formatCurrency(item.amount)}</strong></td>
              </tr>
            `
              )
              .join('')}
          </tbody>
        </table>

        <div class="totals">
          <div class="total-row subtotal">
            <span>Subtotal:</span>
            <span>${formatCurrency(data.subtotal)}</span>
          </div>
          <div class="total-row gst">
            <span>GST (18%):</span>
            <span>${formatCurrency(data.gstAmount)}</span>
          </div>
          <div class="total-row grand-total">
            <span>TOTAL AMOUNT:</span>
            <span>${formatCurrency(data.totalAmount)}</span>
          </div>
        </div>

        <div class="payment-mode">
          <strong>Payment Mode:</strong> ${escHtml(data.paymentMode.toUpperCase())}
        </div>

        <div class="footer">
          <p>Thank you for your visit. Please keep this invoice for your records.</p>
          <p style="margin-top: 10px;">This is a computer-generated invoice and does not require a signature.</p>
        </div>
      </div>
    </body>
    </html>
  `;
}